import { reactive, readonly } from 'vue';

/**
 * cas-236d1: step-up re-authentication for sessions opened from a texted or
 * emailed sign-in link (magic link).
 *
 * Such a session can view and work on content. The backend answers
 * account-security, billing and admin requests with
 * 403 { code: 'STEP_UP_REQUIRED' }. The api client then asks the user to sign
 * in again with a method the account already has, and retries the request
 * once on success. Re-authenticating replaces the Firebase session, so the new
 * ID token no longer carries the link claim.
 */
export const STEP_UP_REQUIRED_CODE = 'STEP_UP_REQUIRED';

export type StepUpMethod = 'password' | 'google';

/**
 * Why the prompt opened. `link`: the session came from a sign-in link and the
 * backend refused a guarded request. `confirm`: the app asks for fresh proof
 * before a sensitive client-side change (login email) on an ordinary session.
 * Only the explanatory line differs; the dialog is the same.
 */
export type StepUpReason = 'link' | 'confirm';

/** ID token claim marking a session opened from a sign-in link (backend step-up.ts). */
export const LINK_SIGNIN_METHOD = 'link';

export function isLinkSignedClaims(claims: unknown): boolean {
  return (
    typeof claims === 'object' &&
    claims !== null &&
    (claims as { signin_method?: unknown }).signin_method === LINK_SIGNIN_METHOD
  );
}

export type StepUpErrorCode = 'wrong-password' | 'popup-closed' | 'user-mismatch' | 'unknown';

export function isStepUpRequiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const record = error as {
    statusCode?: unknown;
    status?: unknown;
    data?: { code?: unknown } | null;
  };
  const status = record.statusCode ?? record.status;
  return status === 403 && record.data?.code === STEP_UP_REQUIRED_CODE;
}

/**
 * Thrown by the api client when the user declines the step-up prompt (Cancel
 * or "Go to sign-in"). Pages show its plain message instead of the raw fetch
 * error ("[GET] http://…: 403 Forbidden").
 */
export class StepUpDeclinedError extends Error {
  readonly statusCode = 403;
  readonly status = 403;
  readonly stepUpDeclined = true;
  readonly data = { code: STEP_UP_REQUIRED_CODE, message: 'Sign in again to continue.' };

  constructor() {
    super('Sign in again to continue.');
    this.name = 'StepUpDeclinedError';
  }
}

export function isStepUpDeclinedError(error: unknown): error is StepUpDeclinedError {
  return (
    error instanceof StepUpDeclinedError ||
    (typeof error === 'object' &&
      error !== null &&
      (error as { stepUpDeclined?: unknown }).stepUpDeclined === true)
  );
}

/**
 * cas-4b78: declining the prompt is the user's choice, not a failure. Callers
 * of guarded endpoints toast their own red "Failed to …" message on any
 * rejection, through many paths ($q.notify, Notify.create, useErrorNotification,
 * useApi). Rather than teach every caller, the api client marks the decline
 * right before it rejects, and a global notify guard drops a negative toast
 * only while that mark stands.
 *
 * The mark is scoped to the declined call's own error handling, never to a
 * time window (QA C04: a retry that really failed 0.65 s later was swallowed):
 *   - the caller's catch runs in the same microtask chain as the rejection, so
 *     its toast is raised while the mark stands;
 *   - the mark clears on the next macrotask (setTimeout 0), and as soon as any
 *     new api request starts, so a later failure, a retry included, always
 *     reports.
 */
let stepUpDeclinePending = false;
let stepUpDeclineTimer: ReturnType<typeof setTimeout> | null = null;

export function clearStepUpDeclined(): void {
  stepUpDeclinePending = false;
  if (stepUpDeclineTimer !== null) {
    clearTimeout(stepUpDeclineTimer);
    stepUpDeclineTimer = null;
  }
}

export function markStepUpDeclined(): void {
  clearStepUpDeclined();
  stepUpDeclinePending = true;
  stepUpDeclineTimer = setTimeout(clearStepUpDeclined, 0);
}

export function wasStepUpJustDeclined(): boolean {
  return stepUpDeclinePending;
}

/** Test hook: forget any recorded decline. */
export const resetStepUpDeclined = clearStepUpDeclined;

type NotifyOptions = { type?: unknown; color?: unknown; classes?: unknown } | string | undefined;

/** True for Quasar notify options that render an error toast. */
export function isNegativeNotify(opts: NotifyOptions): boolean {
  if (!opts || typeof opts !== 'object') return false;
  if (opts.type === 'negative') return true;
  if (typeof opts.color === 'string' && /^(negative|red)(-\d+)?$/.test(opts.color)) return true;
  return typeof opts.classes === 'string' && opts.classes.includes('gabber-toast--negative');
}

/** A negative toast raised by the declined call's own error handling. */
export function shouldSuppressNotify(opts: NotifyOptions): boolean {
  return isNegativeNotify(opts) && wasStepUpJustDeclined();
}

export type NotifyFn = ((opts?: unknown) => unknown) & {
  setDefaults?: unknown;
  registerType?: unknown;
  __stepUpGuard?: true;
};

const noopDismiss = () => undefined;

/** Wrap a Quasar notify function so it drops toasts right after a declined step-up. */
export function guardNotify(original: NotifyFn): NotifyFn {
  if (original.__stepUpGuard) return original;
  const guarded: NotifyFn = (opts?: unknown) =>
    shouldSuppressNotify(opts as Parameters<typeof shouldSuppressNotify>[0])
      ? noopDismiss
      : original(opts);
  guarded.setDefaults = original.setDefaults;
  guarded.registerType = original.registerType;
  guarded.__stepUpGuard = true;
  return guarded;
}

/** Methods the dialog can complete in place, from Firebase providerData ids. */
export function availableStepUpMethods(providerIds: readonly string[]): StepUpMethod[] {
  const methods: StepUpMethod[] = [];
  if (providerIds.includes('password')) methods.push('password');
  if (providerIds.includes('google.com')) methods.push('google');
  return methods;
}

export function toStepUpErrorCode(error: unknown): StepUpErrorCode {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: unknown }).code)
      : error instanceof Error
        ? error.message
        : String(error);
  if (
    code.includes('wrong-password') ||
    code.includes('invalid-credential') ||
    code.includes('invalid-login-credentials')
  ) {
    return 'wrong-password';
  }
  if (code.includes('popup-closed') || code.includes('cancelled-popup-request')) {
    return 'popup-closed';
  }
  if (code.includes('user-mismatch')) return 'user-mismatch';
  return 'unknown';
}

export interface StepUpControllerDeps {
  /** Firebase providerData ids of the signed-in user. */
  getProviderIds: () => string[];
  getEmail: () => string | null;
  /** Re-authenticate the CURRENT Firebase user (never switches accounts). */
  reauthenticate: (method: StepUpMethod, password?: string) => Promise<void>;
  /** Swap the backend session cookie for one minted from the fresh token. */
  refreshSession: () => Promise<void>;
  /** Sign out and send the user to the sign-in page, returning here after. */
  signInAgain: () => Promise<void>;
}

export interface StepUpState {
  open: boolean;
  reason: StepUpReason;
  methods: StepUpMethod[];
  email: string | null;
  busy: boolean;
  error: StepUpErrorCode | null;
}

export function createStepUpController(deps: StepUpControllerDeps) {
  const state = reactive<StepUpState>({
    open: false,
    reason: 'link',
    methods: [],
    email: null,
    busy: false,
    error: null
  });
  let pending: Promise<boolean> | null = null;
  let settle: ((ok: boolean) => void) | null = null;

  const finish = (ok: boolean) => {
    state.open = false;
    state.busy = false;
    const resolve = settle;
    settle = null;
    pending = null;
    resolve?.(ok);
  };

  /**
   * Open the prompt and resolve true once the user has re-authenticated, false
   * if they cancel or leave for the sign-in page. Concurrent callers share one
   * prompt.
   */
  const request = (options: { reason?: StepUpReason } = {}): Promise<boolean> => {
    if (pending) return pending;
    state.reason = options.reason ?? 'link';
    state.methods = availableStepUpMethods(deps.getProviderIds());
    state.email = deps.getEmail();
    state.error = null;
    state.busy = false;
    state.open = true;
    pending = new Promise<boolean>(resolve => {
      settle = resolve;
    });
    return pending;
  };

  const confirm = async (method: StepUpMethod, password?: string): Promise<boolean> => {
    if (!state.open || state.busy) return false;
    state.busy = true;
    state.error = null;
    try {
      await deps.reauthenticate(method, password);
      await deps.refreshSession();
    } catch (error) {
      console.warn('Step-up re-authentication failed:', error);
      state.busy = false;
      state.error = toStepUpErrorCode(error);
      return false;
    }
    finish(true);
    return true;
  };

  const cancel = () => {
    if (state.open) finish(false);
  };

  const signInAgain = async () => {
    finish(false);
    await deps.signInAgain();
  };

  return { state: readonly(state), request, confirm, cancel, signInAgain };
}

export type StepUpController = ReturnType<typeof createStepUpController>;

type ApiRequestLike = (url: string, options: { method: 'POST' }) => Promise<unknown>;

/**
 * Revoke every outstanding sign-in link for the signed-in user. Called after a
 * password or email change, which happen client-side in Firebase and are
 * otherwise invisible to the backend. Best-effort: never throws.
 */
export async function revokeSignInLinks(apiRequest: ApiRequestLike): Promise<void> {
  try {
    await apiRequest('/api/v1/auth/link-sign-in/revoke-all', { method: 'POST' });
  } catch (error) {
    console.warn('Could not revoke outstanding sign-in links:', error);
  }
}
